import React from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { BiPlus, BiTrash, BiLink } from "react-icons/bi";

/**
 * Generated by: ChatGPT-5
 * 
 * LinkPairsEditor (Plain JS + MUI v7)
 *
 * Manages an array shaped like: [[name, url], [name, url], ...]
 *
 * Props
 * - links: Array<[string, string]>
 * - setLinks: (updater: Array<[string, string]> | (prev: Array<[string, string]>) => Array<[string, string]>) => void
 * - maxItems?: number
 */
const UrlListEditor = ({ links, setLinks, maxItems }) => {
  const [nameInput, setNameInput] = React.useState("");
  const [urlInput, setUrlInput] = React.useState("");
  const [urlError, setUrlError] = React.useState("");

  const canAddMore = typeof maxItems === "number" ? links.length < maxItems : true;

  const validateUrl = (value) => {
    if (!value || value.trim().length === 0) return ""; // handled by required check separately
    try {
      const u = new URL(value);
      if (u.protocol !== "https:") {
        return "Use https:// secured links.";
      }
      return "";
    } catch (_) {
      return "Enter a valid URL.";
    }
  };

  const onUrlChange = (e) => {
    const v = e.target.value;
    setUrlInput(v);
    setUrlError(validateUrl(v));
  };

  const resetInputs = () => {
    setNameInput("");
    setUrlInput("");
    setUrlError("");
  };

  const handleAdd = () => {
    if (!nameInput.trim()) return;
    const err = validateUrl(urlInput);
    if (err) { setUrlError(err); return; }

    setLinks((links) => [...links, { name: nameInput.trim(), link: urlInput.trim()}]);
    resetInputs();
  };

  const handleRemove = (index) => {
    setLinks((links) => links.filter((_, i) => i !== index));
  };

  const addDisabled =
    !canAddMore ||
    !nameInput.trim() ||
    !urlInput.trim() ||
    Boolean(validateUrl(urlInput));

  return (
    <Box sx={{ width: "100%" }}>
      {/* Input Row */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }}>
        <TextField
          fullWidth
          label="Description"
          placeholder="e.g., Corporate website, Media article, Sustainability report"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 200 }}}
        />
        <TextField
          fullWidth
          label="URL"
          placeholder="https://example.com/path"
          value={urlInput}
          onChange={onUrlChange}
          error={Boolean(urlError)}
          helperText={urlError || ""}
        />
        <Button
          variant="contained"
          startIcon={<BiPlus />}
          onClick={handleAdd}
          disabled={addDisabled}
        >
          Add
        </Button>
      </Stack>

      {!canAddMore && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          You have reached the maximum number of items.
        </Typography>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "40%" }}>Description</TableCell>
              <TableCell>URL</TableCell>
              <TableCell align="right" sx={{ width: 72 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No links added yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              links.map((item, idx) => (
                <TableRow key={`${item.link}-${idx}`}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BiLink />
                      <Typography variant="body2">{item.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      {item.link}
                    </a>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label={`Remove row ${idx + 1}`} onClick={() => handleRemove(idx)}>
                      <BiTrash />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default UrlListEditor;